/**
 * Real-data messaging store backed by Supabase + Realtime.
 *
 * Keeps the same read/subscribe surface as the previous mockStore so
 * `useMessagingStore` consumers (Inbox, Thread) work without structural
 * changes. Lifecycle:
 *
 *   store.bootstrap(self)          -> load threads + subscribe inbox channel
 *   store.openThread(threadId)     -> load messages + subscribe thread channel
 *   store.closeThread(threadId)    -> tear down thread subscription
 *   store.dispose()                -> tear down inbox subscription
 *
 * Mutations (markRead, setStage, sendMessage) run optimistically and rely
 * on Realtime to deliver the authoritative row.
 */
import type { RealtimeChannel } from '@supabase/supabase-js';
import { toast } from 'react-hot-toast';
import type { Message, MessageAttachment, Stage, Thread, ThreadParticipant } from './types';
import {
  fetchClientThreads,
  fetchDesignerThreads,
  fetchMessages,
  fetchThread,
  insertDesignerMessage,
  insertClientMessage,
  isRateLimitError,
  mapMessage,
  mapThread,
  rpcMarkThreadRead,
  rpcSetThreadStage,
  type DbMessage,
  type DbThread,
} from './api/messagingApi';
import {
  subscribeClientInboxChannel,
  subscribeInboxChannel,
  subscribeThreadChannel,
  unsubscribeChannel,
} from './realtime/channels';
import { mergeIncoming } from './utils/messageMerge';

type Listener = () => void;

/* ---------- module state ---------- */

const listeners = new Set<Listener>();
let revision = 0;

const threadsById: Record<string, Thread> = {};
const messagesByThread: Record<string, Message[]> = {};

let self: ThreadParticipant | null = null;
let selfRole: 'designer' | 'client' = 'designer';
let inboxChannel: RealtimeChannel | null = null;
const threadChannels = new Map<string, RealtimeChannel>();

/**
 * Loading flags consumed by the UI to render skeletons instead of empty
 * states during the initial load. We split them so the inbox can stop
 * showing skeleton rows the instant the threads list resolves, even if a
 * specific thread's messages are still loading.
 *
 *   - `isBootstrapping`: true while `bootstrap()` is in-flight.
 *   - `loadingThreads`: thread ids whose `_refreshThread` hasn't resolved.
 *
 * `loadedThreads` (true once a thread's messages have been fetched at least
 * once) is what the message-area skeleton actually checks: we want to show
 * the skeleton on first open and never again, even if a later refresh races.
 */
let isBootstrapping = false;
const loadingThreads = new Set<string>();
const loadedThreads = new Set<string>();

/* ---------- helpers ---------- */

const notify = () => {
  revision += 1;
  listeners.forEach((l) => l());
};

const upsertThread = (next: Thread) => {
  threadsById[next.id] = next;
};

const upsertThreadRow = (row: DbThread) => {
  if (!self) return;

  if (selfRole === 'client') {
    // Realtime rows arrive without the designer_user join — re-inject the
    // previously-fetched designer profile so the inbox label doesn't regress
    // to a placeholder whenever a message bumps last_message_at.
    if (!row.designer_user && !row.designerUser && !row.designer && threadsById[row.id]) {
      const cachedDesigner = threadsById[row.id].designer;
      row = {
        ...row,
        designer_user: {
          id: cachedDesigner.id,
          full_name: cachedDesigner.fullName,
          username: cachedDesigner.username ?? null,
          profile_picture: cachedDesigner.avatarUrl ?? null,
        },
      };
    }
    upsertThread(mapThread(row, self, 'client'));
  } else {
    // Designer role: carry the previously-hydrated client across Realtime
    // UPDATE rows (which ship the base table without the joined `client`).
    const previousClient = threadsById[row.id]?.client ?? null;
    upsertThread(mapThread(row, self, 'designer', previousClient));
  }
};

const applyIncomingMessage = (threadId: string, msg: Message) => {
  const list = messagesByThread[threadId] ?? [];
  const next = mergeIncoming(list, msg);
  const messagesChanged = next !== list;
  if (messagesChanged) {
    messagesByThread[threadId] = next;
  }

  // Mirror the DB `handle_new_message` trigger locally so the inbox row's
  // preview + lastMessageAt update the instant a message arrives on the
  // thread channel — without waiting for the separate `message_threads`
  // UPDATE echo on the inbox channel (which can be missed during the
  // bootstrap subscribe/fetch race window).
  //
  // We intentionally do NOT touch unread counters here: those come
  // authoritatively from the inbox channel so we don't risk double-counting
  // when both events arrive. Guarded by lastMessageAt so a stale out-of-order
  // delivery can't overwrite a newer preview already applied from the inbox.
  const existing = threadsById[threadId];
  let threadChanged = false;
  if (existing) {
    const msgAt = new Date(msg.createdAt).getTime();
    const threadAt = existing.lastMessageAt ? new Date(existing.lastMessageAt).getTime() : 0;
    if (msgAt > threadAt) {
      const hasBody = msg.body && msg.body.length > 0;
      const hasAttachments = (msg.attachments?.length ?? 0) > 0;
      const preview = hasBody
        ? msg.body.slice(0, 140)
        : hasAttachments
          ? '📷 Photo'
          : existing.lastMessagePreview;
      threadsById[threadId] = {
        ...existing,
        lastMessageAt: msg.createdAt,
        lastMessagePreview: preview,
      };
      threadChanged = true;
    }
  }

  if (messagesChanged || threadChanged) notify();
};

/* ---------- store ---------- */

export const messagingStore = {
  /* reads (sync) */
  listAllThreads(): Thread[] {
    return Object.values(threadsById).sort((a, b) => {
      const ax = a.lastMessageAt ?? a.createdAt;
      const bx = b.lastMessageAt ?? b.createdAt;
      return new Date(bx).getTime() - new Date(ax).getTime();
    });
  },

  getThread(threadId: string): Thread | undefined {
    return threadsById[threadId];
  },

  getMessages(threadId: string): Message[] {
    return messagesByThread[threadId] ?? [];
  },

  unreadTotal(): number {
    return Object.values(threadsById).reduce(
      (s, t) => s + (selfRole === 'client' ? t.unreadForClient || 0 : t.unreadForDesigner || 0),
      0,
    );
  },

  /** True while the initial threads list is loading. */
  isBootstrapping(): boolean {
    return isBootstrapping;
  },

  /** True once a thread's messages have been fetched at least once. */
  hasLoadedMessages(threadId: string): boolean {
    return loadedThreads.has(threadId);
  },

  /** True while a thread's messages are being fetched/refreshed. */
  isLoadingMessages(threadId: string): boolean {
    return loadingThreads.has(threadId);
  },

  revision(): number {
    return revision;
  },

  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },

  /* lifecycle */

  async bootstrap(
    participant: ThreadParticipant,
    options: { role?: 'designer' | 'client' } = {},
  ): Promise<void> {
    const role = options.role ?? 'designer';

    // Re-entrant: no-op if same user + same role is already bootstrapped.
    if (self?.id === participant.id && selfRole === role && inboxChannel) return;

    if (inboxChannel) unsubscribeChannel(inboxChannel);
    self = participant;
    selfRole = role;
    isBootstrapping = true;
    notify();

    const onThreadChange = (row: DbThread, eventType: string) => {
      if (eventType === 'DELETE') {
        delete threadsById[row.id];
        delete messagesByThread[row.id];
        notify();
        return;
      }
      upsertThreadRow(row);
      notify();

      // INSERT events race against the `handle_new_message` trigger UPDATE on
      // the same row. Schedule a delayed re-fetch so the trigger has committed.
      if (eventType === 'INSERT') {
        window.setTimeout(() => {
          fetchThread(row.id)
            .then((latest) => {
              if (latest) {
                upsertThreadRow(latest);
                notify();
              }
            })
            .catch((e) => console.warn('[messaging] post-insert refetch failed', e));
        }, 750);
      }
    };

    const sub =
      role === 'client'
        ? subscribeClientInboxChannel(participant.id, { onThreadChange })
        : subscribeInboxChannel(participant.id, { onThreadChange });
    inboxChannel = sub.channel;

    try {
      await sub.ready;
      const { threads: rows } =
        role === 'client'
          ? await fetchClientThreads()
          : await fetchDesignerThreads(participant.id);
      rows.forEach(upsertThreadRow);
    } catch (e) {
      console.error('[messaging] bootstrap failed', e);
    } finally {
      isBootstrapping = false;
      notify();
    }
  },

  async openThread(threadId: string): Promise<void> {
    if (threadChannels.has(threadId)) {
      // Already open — still refresh messages to catch any gap since last open.
      this._refreshThread(threadId);
      return;
    }

    const sub = subscribeThreadChannel(threadId, {
      onMessageInsert: (row: DbMessage) => applyIncomingMessage(threadId, mapMessage(row)),
    });
    threadChannels.set(threadId, sub.channel);
    // Same rationale as bootstrap: wait for the subscription ACK so an
    // INSERT during the connect window isn't dropped.
    await sub.ready;
    await this._refreshThread(threadId);
  },

  closeThread(threadId: string): void {
    const ch = threadChannels.get(threadId);
    if (ch) {
      unsubscribeChannel(ch);
      threadChannels.delete(threadId);
    }
  },

  dispose(): void {
    threadChannels.forEach((ch) => unsubscribeChannel(ch));
    threadChannels.clear();
    if (inboxChannel) unsubscribeChannel(inboxChannel);
    inboxChannel = null;
    self = null;
    selfRole = 'designer';
    // Clear cached state so a different account signing in on the same
    // device doesn't see the previous user's threads or messages.
    Object.keys(threadsById).forEach((k) => delete threadsById[k]);
    Object.keys(messagesByThread).forEach((k) => delete messagesByThread[k]);
    loadingThreads.clear();
    loadedThreads.clear();
    isBootstrapping = false;
    notify();
  },

  /* mutations */

  /**
   * Patch the designer participant on a thread when the backend response
   * didn't return the full designer_user join (e.g. freshly-created threads
   * via POST /messaging/threads). This fixes the inbox display name + avatar.
   */
  patchThreadDesigner(
    threadId: string,
    patch: { fullName?: string; avatarUrl?: string | null; username?: string },
  ): void {
    const t = threadsById[threadId];
    if (!t) return;
    const updated: Thread = {
      ...t,
      designer: { ...t.designer, ...patch },
    };
    threadsById[threadId] = updated;
    notify();
  },

  async markRead(threadId: string): Promise<void> {
    const t = threadsById[threadId];
    if (t && t.unreadForDesigner > 0) {
      threadsById[threadId] = { ...t, unreadForDesigner: 0 };
      notify();
    }
    try {
      await rpcMarkThreadRead(threadId);
    } catch (e) {
      console.warn('[messaging] mark_thread_read failed', e);
    }
  },

  async setStage(threadId: string, stage: Stage): Promise<void> {
    const prev = threadsById[threadId];
    if (prev) {
      threadsById[threadId] = { ...prev, stage };
      notify();
    }
    try {
      await rpcSetThreadStage(threadId, stage);
      // Realtime echo delivers the system stage-transition message + updated row.
    } catch (e) {
      console.error('[messaging] set_thread_stage failed', e);
      if (prev) {
        threadsById[threadId] = prev;
        notify();
      }
    }
  },

  async sendMessage(input: {
    threadId: string;
    body: string;
    attachments?: MessageAttachment[];
    metadata?: Record<string, unknown>;
  }): Promise<void> {
    if (!self) {
      console.warn('[messaging] sendMessage called before bootstrap');
      return;
    }
    const tempId = `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const optimistic: Message = {
      id: tempId,
      threadId: input.threadId,
      senderRole: selfRole === 'client' ? 'client' : 'designer',
      senderId: self.id,
      body: input.body,
      attachments: input.attachments,
      metadata: input.metadata,
      createdAt: new Date().toISOString(),
      pending: true,
      clientTempId: tempId,
    };
    // Route through applyIncomingMessage so the inbox summary (preview +
    // lastMessageAt) mirrors the message immediately — same path the Realtime
    // echo will hit later.
    applyIncomingMessage(input.threadId, optimistic);

    try {
      // Route to the correct insert function based on the viewer's role so
      // the RLS policy (sender_role must match the thread participant slot) passes.
      if (selfRole === 'client') {
        await insertClientMessage({
          threadId: input.threadId,
          senderId: self.id,
          body: input.body,
          attachments: input.attachments,
          clientTempId: tempId,
          metadata: input.metadata,
        });
      } else {
        await insertDesignerMessage({
          threadId: input.threadId,
          senderId: self.id,
          body: input.body,
          attachments: input.attachments,
          clientTempId: tempId,
          metadata: input.metadata,
        });
      }
      // Authoritative row arrives via Realtime INSERT and reconciles by clientTempId.
    } catch (e) {
      // R1.5 added a DB-level 60/min/sender cap; surface it explicitly so the
      // designer knows why the send failed.
      if (isRateLimitError(e)) {
        toast.error('You’re sending too fast. Try again in a minute.');
      } else {
        console.error('[messaging] send failed', e);
      }
      messagesByThread[input.threadId] = (messagesByThread[input.threadId] ?? []).map((m) =>
        m.id === tempId
          ? { ...m, pending: false, metadata: { ...(m.metadata ?? {}), failed: true } }
          : m,
      );
      notify();
    }
  },

  /* internal */
  async _refreshThread(threadId: string): Promise<void> {
    loadingThreads.add(threadId);
    notify();
    try {
      const [tRow, mRows] = await Promise.all([fetchThread(threadId), fetchMessages(threadId)]);
      if (tRow) upsertThreadRow(tRow);
      const mapped = mRows.map(mapMessage);
      messagesByThread[threadId] = mapped;
      loadedThreads.add(threadId);

      // If the thread row didn't have a lastMessagePreview (DB trigger lag or
      // brand-new thread), backfill from the most recent message so the inbox
      // never shows "No messages yet" when messages actually exist.
      const cached = threadsById[threadId];
      if (cached && !cached.lastMessagePreview && mapped.length > 0) {
        const last = mapped[mapped.length - 1];
        threadsById[threadId] = {
          ...cached,
          lastMessageAt: cached.lastMessageAt ?? last.createdAt,
          lastMessagePreview: last.body || (last.attachments?.length ? '📎 Attachment' : ''),
        };
      }
    } catch (e) {
      console.error('[messaging] load thread failed', e);
    } finally {
      loadingThreads.delete(threadId);
      notify();
    }
  },
};

export type MessagingStore = typeof messagingStore;
